/**
 * F5 (move & store) command payloads, as sent in `commands[].payload` of POST /sync/commands.
 *
 * Wire names follow the spec's engineering.api bodies (F5.1, F5.2, F5.3, F5.4, F5.6, F5.9,
 * F5-B1.a). The spec bodies also list `command_id` and `occurred_at`: those are the command
 * envelope's `id` and `occurred_at` (offline/protocol.ts) and are NOT repeated in the payload.
 *
 * Weights: the spec's measurement `value` with `uom: 'KG'` is sent as an exact decimal string with
 * 3 decimals ("45.300", made from integer grams — never a float), and `value_g` (integer grams,
 * R27) rides beside it so the server never has to parse.
 *
 * Importing this module registers every schema, so `enqueueCommand` refuses a malformed payload on
 * the phone.
 */
import { z } from 'zod'
import { registerCommand } from '@/offline'
import { gramsToKgInput, type Grams } from '@/lib/weight'

const id = z.string().min(1)
const isoTs = z.string().min(1)
const kgString = z.string().regex(/^\d{1,5}\.\d{3}$/, 'kg with 3 decimals')

/** Reason codes the phone refuses a scan with (F5.1, F5.2, F5.3 validations). */
export const SCAN_REFUSALS = [
  'TAG_WRONG_KIND',
  'TAG_NOT_BOUND',
  'TAG_VOID',
  'TAG_RETIRED',
  'TAG_UNKNOWN',
  'NOT_AT_ORIGIN',
  'ALREADY_LOADED',
  'VEHICLE_NOT_CLEAN',
  'NOT_STORED',
  'LEVEL_NOT_ALLOWED',
  'LOCATION_INACTIVE',
  'OTHER_SITE',
  'NOT_DECLARED',
  'CONV_IN_CLEAN_BAY',
  'POSITION_HAS_OTHER_LOT',
  'SAME_PLACE',
  'NOT_A_BAY',
  'NOT_SAMPLE_STORE',
  'NOT_YOUR_ROLL',
  'TAG_ALREADY_USED',
  'OLD_TAG_NOT_HERE',
] as const
export type ScanRefusal = (typeof SCAN_REFUSALS)[number]

const refusedScan = z.object({
  qr_payload: z.string().min(1),
  reason: z.enum(SCAN_REFUSALS),
  scanned_at: isoTs,
})

// ---------------------------------------------------------------------------------------------
// F5.1 Load and scan out
// ---------------------------------------------------------------------------------------------

export const loadBagsPayload = z.object({
  shipment: z.object({
    id,
    /** MV-{YYMMDD}-{device prefix}-{nn}, made on the phone. */
    code: z.string().min(1),
    kind: z.literal('TRANSFER'),
    from_location_id: id,
    to_location_id: id,
    vehicle_no: z.string().min(1).max(20),
    // Seva Mandir V1 types only the destination and the vehicle (F5.1); the server takes these
    // as optional (load_bags validate), the full flow still asks for every one of them.
    driver_name: z.string().min(1).max(80).optional(),
    driver_phone: z
      .string()
      .regex(/^\d{10}$/)
      .optional(),
    challan_no: z.string().min(1).max(30).optional(),
    /** Left out when nobody answered the bed check (V1): no VEHICLE_NOT_CLEAN at sync. */
    vehicle_clean: z.boolean().optional(),
  }),
  items: z
    .array(
      z
        .object({
          id,
          qr_payload: z.string().min(1),
          container_id: id.optional(),
          sample_id: id.optional(),
          loaded_at: isoTs,
        })
        .refine((i) => Boolean(i.container_id) !== Boolean(i.sample_id), {
          message: 'exactly one of container_id and sample_id',
        }),
    )
    .min(1),
  refused_scans: z.array(refusedScan).default([]),
})
export type LoadBagsPayload = z.infer<typeof loadBagsPayload>

export const dispatchShipmentPayload = z.object({
  shipment_id: id,
  hand_count: z.number().int().min(0),
  /** The loaded-vehicle photo; V1 takes none (the server takes it as optional). */
  photo_id: id.optional(),
  partial_unit_ids: z.array(id).default([]),
  dispatched_at: isoTs,
})
export type DispatchShipmentPayload = z.infer<typeof dispatchShipmentPayload>

// ---------------------------------------------------------------------------------------------
// F5.2 Receive
// ---------------------------------------------------------------------------------------------

export const MISSING_REASONS = ['NOT_ON_VEHICLE', 'TAG_UNREADABLE', 'DAMAGED_IN_TRANSIT'] as const
export type MissingReason = (typeof MISSING_REASONS)[number]

export const checkWeight = z.object({
  measurement_id: id,
  container_id: id,
  value: kgString,
  value_g: z.number().int().positive(),
  uom: z.literal('KG'),
  method: z.literal('MANUAL_ENTRY'),
  equipment_id: id.nullable(),
  taken_at: isoTs,
  picked_by: z.enum(['SERVER', 'PHONE']),
})
export type CheckWeight = z.infer<typeof checkWeight>

export const receiveShipmentPayload = z.object({
  shipment_id: id,
  received_at: isoTs,
  unloads: z.array(z.object({ item_id: id, unloaded_at: isoTs })),
  extras: z.array(z.object({ item_id: id, qr_payload: z.string().min(1), unloaded_at: isoTs })),
  check_weights: z.array(checkWeight),
  /** V1 asks no reason (one confirm); the server takes reason_code as optional. */
  missing: z.array(z.object({ item_id: id, reason_code: z.enum(MISSING_REASONS).optional() })),
  refused_scans: z.array(refusedScan.extend({ photo_id: id.nullable() })),
})
export type ReceiveShipmentPayload = z.infer<typeof receiveShipmentPayload>

// ---------------------------------------------------------------------------------------------
// F5.3 Put away · F5.9 Move — same body, same handler
// ---------------------------------------------------------------------------------------------

const placeItem = z
  .object({
    container_id: id.optional(),
    sample_id: id.optional(),
    qr_payload: z.string().min(1),
    scanned_at: isoTs,
  })
  .refine((i) => Boolean(i.container_id) !== Boolean(i.sample_id), {
    message: 'exactly one of container_id and sample_id',
  })

export const putAwayPayload = z.object({
  location_id: id,
  /** The location code when it was typed instead of scanned (payload.manual_entry), else null. */
  typed_code: z.string().min(1).nullable(),
  items: z.array(placeItem).min(1),
})
export type PutAwayPayload = z.infer<typeof putAwayPayload>
export const moveBagsPayload = putAwayPayload
export type MoveBagsPayload = PutAwayPayload

// ---------------------------------------------------------------------------------------------
// F5.4 Storage declaration
// ---------------------------------------------------------------------------------------------

export const STORAGE_FORM_CODE = 'STORAGE_NO_FUMIGANT'

export const signBayDeclarationPayload = z.object({
  declaration: z.object({
    id,
    subject_type: z.literal('LOCATION'),
    subject_id: id,
    form_code: z.literal(STORAGE_FORM_CODE),
    form_version: z.number().int().positive(),
    answers: z.record(z.string(), z.union([z.boolean(), z.number(), z.null()])),
    photo_id: id,
    signed_at: isoTs,
  }),
})
export type SignBayDeclarationPayload = z.infer<typeof signBayDeclarationPayload>

// ---------------------------------------------------------------------------------------------
// F5.6 Count a place · red ties
// ---------------------------------------------------------------------------------------------

export const countBayPayload = z.object({
  count: z.object({
    id,
    location_id: id,
    qr_payloads: z.array(z.string().min(1)),
    started_at: isoTs,
    ended_at: isoTs,
  }),
})
export type CountBayPayload = z.infer<typeof countBayPayload>

export const markBagsConvPayload = z.object({
  items: z
    .array(z.object({ container_id: id, qr_payload: z.string().min(1), scanned_at: isoTs }))
    .min(1),
})
export type MarkBagsConvPayload = z.infer<typeof markBagsConvPayload>

// ---------------------------------------------------------------------------------------------
// F5-B1.a Torn bag / lost tag
// ---------------------------------------------------------------------------------------------

export const replaceContainerPayload = z.object({
  count_id: id,
  location_id: id,
  reason: z.enum(['TORN', 'TAG_LOST']),
  old_qr_payload: z.string().min(1).nullable(),
  new_container: z.object({ id, qr_payload: z.string().min(1), sack_type_id: id }),
  measurement: z.object({
    id,
    value: kgString,
    value_g: z.number().int().positive(),
    uom: z.literal('KG'),
    method: z.literal('MANUAL_ENTRY'),
    taken_at: isoTs,
  }),
  photo_id: id,
  crop_id: id.nullable(),
})
export type ReplaceContainerPayload = z.infer<typeof replaceContainerPayload>

registerCommand('load_bags', loadBagsPayload)
registerCommand('dispatch_shipment', dispatchShipmentPayload)
registerCommand('receive_shipment', receiveShipmentPayload)
registerCommand('put_away', putAwayPayload)
registerCommand('move_bags', moveBagsPayload)
registerCommand('sign_bay_declaration', signBayDeclarationPayload)
registerCommand('count_bay', countBayPayload)
registerCommand('mark_bags_conv', markBagsConvPayload)
registerCommand('replace_container', replaceContainerPayload)

/** Integer grams → the wire's exact kg string ("45.300"). */
export function kgWire(g: Grams): string {
  return gramsToKgInput(g)
}

export const STORE_COMMAND_TYPES = [
  'load_bags',
  'dispatch_shipment',
  'receive_shipment',
  'put_away',
  'move_bags',
  'sign_bay_declaration',
  'count_bay',
  'mark_bags_conv',
  'replace_container',
] as const
export type StoreCommandType = (typeof STORE_COMMAND_TYPES)[number]
